import { Avatar, MenuItem } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import StyledTextField from "../../StyledTextField/StyledTextField";

interface Company {
    companyId: number;
    name: string;
    avatarUrl?: string | null;
}

interface CompanySwitcherProps {
    /** List of available companies */
    companies: Company[];
    /** Currently active company ID */
    currentCompanyId?: number;
    /** Callback when company is switched */
    onSwitch: (companyId: number, shouldReload: boolean) => void;
}

/**
 * Company switcher component for users with multiple companies
 * Includes search functionality for large company lists
 */
const CompanySwitcher = ({ companies, currentCompanyId, onSwitch }: CompanySwitcherProps) => {
    const { t } = useTranslation();
    const [filterText, setFilterText] = useState("");
    const filterRef = useRef<HTMLInputElement | null>(null);

    const filteredCompanies = useMemo(
        () => companies.filter((company) => company.name.toLowerCase().includes(filterText.toLowerCase())),
        [companies, filterText],
    );

    const handleFilterKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            filterRef.current?.blur();
        }
    };

    const showSearch = companies.length > 5;

    return (
        <div tabIndex={-1}>
            <div className="navigation-menu__company-header">{t("NavigationBar.SwitchCompany")}</div>

            {showSearch && (
                <StyledTextField
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    onKeyDown={handleFilterKeyDown}
                    fullWidth
                    size="small"
                    placeholder={t("NavigationBar.SearchCompany")}
                    sx={{ padding: "0 8px" }}
                    trueInputRef={filterRef}
                />
            )}

            {filteredCompanies.map((company) => (
                <MenuItem
                    key={company.companyId}
                    onClick={() => onSwitch(company.companyId, true)}
                    sx={{ fontFamily: "Poppins" }}
                    className={company.companyId === currentCompanyId ? "navigation-menu__company-item--active" : ""}
                    style={
                        company.companyId === currentCompanyId
                            ? { backgroundColor: "#FAF9FF", pointerEvents: "none" }
                            : {}
                    }
                >
                    <Avatar
                        src={company.avatarUrl ?? ""}
                        variant="rounded"
                        className="navigation-menu__company-avatar"
                        sx={{
                            width: 24,
                            height: 24,
                            marginRight: "8px",
                            fontSize: "12px",
                            color: "black",
                            backgroundColor: "#FFF2F1",
                        }}
                    >
                        {company.name[0]?.toUpperCase()}
                    </Avatar>
                    {company.name}
                </MenuItem>
            ))}
        </div>
    );
};

export default CompanySwitcher;
